import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

let polices: Promise<{ arabeRegular: string; arabeBold: string; serif: string; naskhRegular: string; naskhBold: string; liberationSerifBold: string }> = null;

function chargerCommeBase64(url: string): Promise<string> {
	return fetch(url)
		.then(r => r.arrayBuffer())
		.then(buffer => {
			let binaire = '';
			const octets = new Uint8Array(buffer);
			for (let i = 0; i < octets.length; i++) {
				binaire += String.fromCharCode(octets[i]);
			}
			return btoa(binaire);
		});
}

/**
 * Enregistre les polices pdfMake pour l'ordonnance « avec entité » : Roboto (texte courant, par défaut) +
 * Aref Ruqaa (nom/titre du vétérinaire en arabe — style calligraphié manuscrit, choisi par le client) + Noto
 * Serif en gras italique (nom/titre en français, même logique) + Noto Naskh Arabic (adresse du pied de page en
 * arabe — le client a demandé « Traditional Arabic », police Microsoft propriétaire indisponible ; Noto Naskh
 * Arabic est l'équivalent libre le plus proche, même registre « naskh d'impression classique ») + Liberation
 * Serif (label « R9 » du pied de page — le client a demandé Times New Roman, propriétaire ; Liberation Serif
 * est son équivalent libre, compatible au point près). Chargées en fichiers statiques (`assets/fonts/`) et
 * converties en base64 à la demande — jamais embarquées dans le bundle JS : à ~110-660 Ko par fichier, les
 * inclure directement dans un module TypeScript avait fait gonfler le bundle initial de l'app de plusieurs Mo
 * (constaté à la compilation le 2026-09-23), un budget déjà serré vu l'historique d'hébergement LWS de ce
 * projet. Seule la variante gras-italique de Noto Serif et la variante grasse de Liberation Serif sont
 * chargées (seuls styles utilisés ici).
 *
 * Aref Ruqaa ET Noto Naskh Arabic ne couvrent pas la lettre maghrébine « ڨ » (qaf à 3 points, son « G » de
 * « Gall ») — un qaf standard (ق) est utilisé à la place dans `ARABE_NOM`.
 *
 * Le TÉLÉCHARGEMENT des polices n'a lieu qu'une fois (mis en cache dans `polices`), mais `pdfMake.vfs`/`.fonts`
 * sont RÉAFFECTÉS à chaque appel : `pdfMake` est un singleton partagé avec examen.component.ts, qui réaffecte
 * lui-même `pdfMake.vfs` (sans ces polices) à chaque PDF d'examen généré — sans cette réaffectation
 * systématique, générer un examen entre deux ordonnances « avec entité » effacerait silencieusement les
 * polices déjà chargées et ferait échouer la génération suivante.
 */
export function pdfMakeOrdonnance(): Promise<typeof pdfMake> {
	if (!polices) {
		polices = Promise.all([
			chargerCommeBase64('assets/fonts/ArefRuqaa-Regular.ttf'),
			chargerCommeBase64('assets/fonts/ArefRuqaa-Bold.ttf'),
			chargerCommeBase64('assets/fonts/NotoSerif-BoldItalic.ttf'),
			chargerCommeBase64('assets/fonts/NotoNaskhArabic-Regular.ttf'),
			chargerCommeBase64('assets/fonts/NotoNaskhArabic-Bold.ttf'),
			chargerCommeBase64('assets/fonts/LiberationSerif-Bold.ttf'),
		]).then(([arabeRegular, arabeBold, serif, naskhRegular, naskhBold, liberationSerifBold]) =>
			({ arabeRegular, arabeBold, serif, naskhRegular, naskhBold, liberationSerifBold }));
	}
	return polices.then(({ arabeRegular, arabeBold, serif, naskhRegular, naskhBold, liberationSerifBold }) => {
		pdfMake.vfs = {
			...pdfFonts.pdfMake.vfs,
			'ArefRuqaa-Regular.ttf': arabeRegular,
			'ArefRuqaa-Bold.ttf': arabeBold,
			'NotoSerif-BoldItalic.ttf': serif,
			'NotoNaskhArabic-Regular.ttf': naskhRegular,
			'NotoNaskhArabic-Bold.ttf': naskhBold,
			'LiberationSerif-Bold.ttf': liberationSerifBold,
		};
		pdfMake.fonts = {
			Roboto: {
				normal: 'Roboto-Regular.ttf',
				bold: 'Roboto-Medium.ttf',
				italics: 'Roboto-Italic.ttf',
				bolditalics: 'Roboto-MediumItalic.ttf',
			},
			ArefRuqaa: {
				normal: 'ArefRuqaa-Regular.ttf',
				bold: 'ArefRuqaa-Bold.ttf',
				italics: 'ArefRuqaa-Regular.ttf',
				bolditalics: 'ArefRuqaa-Bold.ttf',
			},
			NotoSerifEntite: {
				normal: 'NotoSerif-BoldItalic.ttf',
				bold: 'NotoSerif-BoldItalic.ttf',
				italics: 'NotoSerif-BoldItalic.ttf',
				bolditalics: 'NotoSerif-BoldItalic.ttf',
			},
			NotoNaskhArabic: {
				normal: 'NotoNaskhArabic-Regular.ttf',
				bold: 'NotoNaskhArabic-Bold.ttf',
				italics: 'NotoNaskhArabic-Regular.ttf',
				bolditalics: 'NotoNaskhArabic-Bold.ttf',
			},
			LiberationSerifBold: {
				normal: 'LiberationSerif-Bold.ttf',
				bold: 'LiberationSerif-Bold.ttf',
				italics: 'LiberationSerif-Bold.ttf',
				bolditalics: 'LiberationSerif-Bold.ttf',
			},
		};
		return pdfMake;
	});
}
